// index.js
import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import "./index.css";
import { Select, DatePicker, message, Typography, Switch } from "antd";
import moment from "moment";
import "moment/locale/pt-br";
moment.locale("pt-br");
import {
  companiesService,
  dashboardBankHoursService,
  dashboardExtraHoursService,
  departmentsService
} from "./../../../redux/services";

// --- CONSTANTES DE LAYOUT ---
const BAR_SIZE = 8;
const ROW_GAP = 24;
const MAX_DAYS = 31; // limite inclusivo

// --- HELPERS ---
const toNumber = (v) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (v == null) return 0;
  const s = String(v).trim().replace(/[^\d,.\-]/g, "");
  const normalized = s.replace(/\.(?=\d{3}(?:\D|$))/g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

// normaliza para: sem futuro, máximo 31 dias (inclusivo)
function normalizeRange(vals) {
  const TODAY_END = moment().endOf("day");
  let [start, end] = vals || [];

  end = moment.isMoment(end) ? end.clone().endOf("day") : TODAY_END.clone();
  if (end.isAfter(TODAY_END)) end = TODAY_END.clone();

  start = moment.isMoment(start) ? start.clone().startOf("day") : end.clone().subtract(MAX_DAYS - 1, "days").startOf("day");

  const inclusiveDays = end.diff(start, "days") + 1;
  if (inclusiveDays > MAX_DAYS) {
    end = start.clone().add(MAX_DAYS - 1, "days").endOf("day");
  }

  return [start, end];
}

function clampRange(vals) {
  // alias para deixar semântica clara ao usar em onChange
  const [s, e] = normalizeRange(vals);
  const inclusiveDays = e.diff(s, "days") + 1;
  if (inclusiveDays > MAX_DAYS) {
    message.info(`Intervalo máximo é de ${MAX_DAYS} dias.`);
  }
  return [s, e];
}

// --- PORTAL DO MINI-MODAL ---
function FilterPopover({ anchorRef, open, onClose, children }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, maxW: 360 });

  function placePanel() {
    const btn = anchorRef?.current;
    const panel = panelRef.current;
    if (!btn || !panel) return;

    const t = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const gap = 8;
    const margin = 8;

    const pw = panel.offsetWidth || 320;
    const ph = panel.offsetHeight || 0;

    // bottom-end (alinha à direita do botão)
    let left = t.right - pw;
    let top = t.bottom + gap;

    // clamp horizontal
    if (left < margin) left = margin;
    if (left + pw + margin > vw) left = vw - pw - margin;

    // flip vertical se estourar embaixo
    if (top + ph + margin > vh) {
      const above = t.top - ph - gap;
      top = Math.max(margin, above);
    }

    const maxW = Math.min(360, vw - margin * 2);
    setPos({ left, top, maxW });
  }

  useLayoutEffect(() => {
    if (!open) return;
    placePanel();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onScroll = () => placePanel();
    const onResize = () => placePanel();
    const onDown = (e) => {
      const p = panelRef.current;
      const a = anchorRef?.current;
      if (p && !p.contains(e.target) && a && !a.contains(e.target)) onClose?.();
    };
    const onKey = (e) => e.key === "Escape" && onClose?.();

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        zIndex: 99999,
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
        border: "1px solid #EEF2F6",
        padding: 16,
        minWidth: 280,
        maxWidth: pos.maxW,
        width: "max-content"
      }}
      role="dialog"
      aria-modal="true"
    >
      {typeof children === "function" ? children(panelRef) : children}
    </div>,
    document.body
  );
}

// --- COMPONENTE PRINCIPAL ---
export default function HoursBank({
  endPoint = "banco_horas",
  title = "Banco de horas",
}) {
  const TODAY_END = useMemo(() => moment().endOf("day"), []);
  const DEFAULT_START = useMemo(() => moment().subtract(MAX_DAYS - 1, "days").startOf("day"), []);

  // --- ESTADOS ---
  const [emp, setEmp] = useState("all");
  const [dept, setDept] = useState("");
  const [modalFilter, setModalFilter] = useState(false);
  const [empDraft, setEmpDraft] = useState("all");
  const [deptDraft, setDeptDraft] = useState("");

  // range padrão: últimos 31 dias
  const [range, setRange] = useState([DEFAULT_START.clone(), TODAY_END.clone()]);
  const [rangeDraft, setRangeDraft] = useState([DEFAULT_START.clone(), TODAY_END.clone()]);

  // groupby padrão: empresa
  const [group, setGroup] = useState("empresa");

  // limites dinâmicos durante a seleção no calendário
  const [limit, setLimit] = useState({ min: null, max: null });

  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [empresas, setEmpresas] = useState([{ label: "Todas as Empresas", value: "all" }]);
  const [departamentos, setDepartamentos] = useState([{ label: "Todos os Departamentos", value: "" }]);
  const [columns, setColumns] = useState([]);
  const isAll = (v) => v === "" || v === "all" || v == null;
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);

  const hasDynamicCols = Array.isArray(columns) && columns.length > 0;

  // --- DADOS / ROWS ---
  const rows = useMemo(() => {
    const arr = Array.isArray(chartData) ? chartData : [];
    if (!hasDynamicCols) {
      return arr.map((d, i) => ({
        name: d.name ?? `Item ${i + 1}`,
        credito: d.credito,
        debito: d.debito,
        saldo: d.saldo,
        ref_id: d.ref_id ?? `item-${i}`,
      }));
    }
    return arr.map((d, i) => {
      const base = {
        name: d.name ?? `Item ${i + 1}`,
        ref_id: d.ref_id ?? `item-${i}`,
      };
      columns.forEach((c) => {
        base[c.key] = d[c.key];
      });
      return base;
    });
  }, [chartData, columns, hasDynamicCols]);

  const getBarValue = (row) => {
    if (hasDynamicCols) {
      return columns.reduce((sum, c) => sum + Math.abs(toNumber(row[c.key])), 0);
    }
    return Math.abs(toNumber(row.saldo));
  };

  const maxAbsValue = useMemo(() => {
    const vals = rows.map(getBarValue);
    const max = Math.max(0, ...vals);
    return max > 0 ? max : 1;
  }, [rows, columns, hasDynamicCols]);

  // --- LOADERS ---
  async function carregaDados(
    nextEmp = emp,
    nextDept = dept,
    nextRange = range,
    nextGroup = group,
  ) {
    setLoadingChart(true);
    try {
      const options = {};
      if (!isAll(nextEmp)) options.empresa_uuid = nextEmp;
      if (!isAll(nextDept)) options.departamento_uuid = nextDept;
      if (!isAll(nextGroup)) options.group_by = nextGroup;
      if (Array.isArray(nextRange) && nextRange[0] && nextRange[1]) {
        options.inicio = nextRange[0].format("YYYY-MM-DD");
        options.fim = nextRange[1].format("YYYY-MM-DD");
      }

      const resp = endPoint === "banco_horas"
        ? await dashboardBankHoursService.getAll(options)
        : await dashboardExtraHoursService.getAll(options);

      const dados = resp?.data?.data ?? resp?.data ?? resp ?? [];
      const cols = resp?.data?.columns ?? resp?.columns ?? [];

      setChartData(Array.isArray(dados) ? dados : []);
      setColumns(Array.isArray(cols) ? cols : []);
    } catch (e) {
      console.error("Erro ao carregar dados do gráfico", e);
      setChartData([]);
      setColumns([]);
    } finally {
      setLoadingChart(false);
    }
  }

  async function carregarDepartamentos(empUuid) {
    setLoadingDepartamentos(true);
    try {
      const options = {};
      if (!isAll(empUuid)) options.empresa_uuid = empUuid;
      const resp = await departmentsService.getAll(options);
      const brutos = resp?.data?.data ?? resp?.data ?? resp ?? [];
      const lista = (Array.isArray(brutos) ? brutos : []).map((d) => ({ label: d.nome, value: d.uuid }));
      setDepartamentos(lista.length > 0 ? [{ label: "Todos os Departamentos", value: "" }, ...lista] : []);
    } catch (e) {
      console.error("Erro ao carregar departamentos", e);
      setDepartamentos([]);
    } finally {
      setLoadingDepartamentos(false);
    }
  }

  // --- INIT ---
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      await carregaEmpresas();
      await carregaDados(emp, dept, range, group);
    })();
  }, []);

  async function carregaEmpresas() {
    setLoadingEmpresas(true);
    try {
      const response = await companiesService.getAll();
      const brutos = response?.data?.data ?? response?.data ?? response ?? [];
      const lista = (Array.isArray(brutos) ? brutos : []).map((d) => ({
        label: d.nome,
        value: d.uuid,
      }));
      setEmpresas((prev) => [prev[0], ...lista]);
    } catch (e) {
      console.error("Erro ao carregar empresas", e);
      setEmpresas([{ label: "Todas as empresas", value: "all" }]);
    } finally {
      setLoadingEmpresas(false);
    }
  }

  // --- MODAL FILTER STATES/LOGIC ---
  useEffect(() => {
    if (!modalFilter) return;
    setDeptDraft("");
    if (isAll(empDraft)) {
      setDepartamentos([{ label: "Todos os Departamentos", value: "" }]);
    } else {
      carregarDepartamentos(empDraft);
    }
  }, [empDraft, modalFilter]);

  useEffect(() => {
    if (!modalFilter) return;

  }, [deptDraft, modalFilter, empDraft]);

  const filterBtnRef = useRef(null);

  const handleApply = async () => {
    const nextEmp = empDraft || "all";
    const nextDept = deptDraft || "";
    const nextRange = normalizeRange(rangeDraft);
    setEmp(nextEmp);
    setDept(nextDept);
    setRange(nextRange);
    await carregaDados(nextEmp, nextDept, nextRange);
    setModalFilter(false);
  };

  // --- GRID CONFIG DINÂMICA
  const valueCols = hasDynamicCols ? columns.map(c => c.key) : ["debito", "credito", "saldo"];
  const headerLabels = hasDynamicCols ? columns.map(c => c.name || c.key) : ["Débito", "Crédito", "Saldo"];
  const templateColumns = `1fr 1fr ${Array(valueCols.length).fill("0.5fr").join(" ")}`;

  // --- Calendário: disabledDate e limites dinâmicos de 31 dias
  const disabledDate = (current) => {
    if (!current) return false;
    // não permite futuro
    if (current.isAfter(moment().endOf("day"), "day")) return true;
    // quando uma ponta foi escolhida, prende a outra ponta à janela de ±30 dias
    if (limit.min && current.isBefore(limit.min, "day")) return true;
    if (limit.max && current.isAfter(limit.max, "day")) return true;
    return false;
  };

  return (
    <div className="hours-card">
      {/* --- CABEÇALHO DO CARD --- */}
      <div className="hours-card-inner">
        <div className="hours-card-title">
          <div className="hours-title">{title}</div>
          <div className="hours-subtitle">
            {range?.[0]?.format("DD/MM/YYYY")} – {range?.[1]?.format("DD/MM/YYYY")}
          </div>
        </div>

        {/* Ícone que ancora o popover */}
        <div className="filter-icon" style={{ position: "absolute", right: 0, top: 0, zIndex: 23 }}>
          <i
            ref={filterBtnRef}
            onClick={() => setModalFilter(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setModalFilter(true)}
            className="fas fa-filter"
            style={{ fontSize: 18, cursor: "pointer", color: "#00C9FF" }}
            aria-haspopup="dialog"
            aria-expanded={modalFilter}
          />
        </div>
      </div>

      {/* --- MINI-MODAL via PORTAL --- */}
      <FilterPopover
        anchorRef={filterBtnRef}
        open={modalFilter}
        onClose={() => setModalFilter(false)}
      >
        {(panelRef) => (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Select
              value={empDraft}
              onChange={(v) => {
                setEmpDraft(v);
                v != "" ? setGroup("departamento") : setGroup("empresa");
              }}
              options={empresas}
              size="middle"
              style={{ width: "100%" }}
              loading={loadingEmpresas}
              getPopupContainer={() => panelRef.current || document.body}
            />
            <Select
              value={deptDraft}
              onChange={setDeptDraft}
              options={departamentos}
              size="middle"
              style={{ width: "100%" }}
              loading={loadingDepartamentos}
              disabled={isAll(empDraft)}
              placeholder="Departamento"
              notFoundContent="Nenhum departamento encontrado"
              getPopupContainer={() => panelRef.current || document.body}
            />
            <DatePicker.RangePicker
              value={rangeDraft}
              // ao abrir/selecionar a 1ª data, definimos janela móvel de ±30 dias
              onCalendarChange={(dates) => {
                if (dates && dates[0] && !dates[1]) {
                  const first = dates[0].clone().startOf("day");
                  setLimit({
                    min: first.clone().subtract(MAX_DAYS - 1, "days").startOf("day"),
                    max: first.clone().add(MAX_DAYS - 1, "days").endOf("day"),
                  });
                } else {
                  setLimit({ min: null, max: null });
                }
              }}
              onChange={(vals) => setRangeDraft(clampRange(vals))}
              disabledDate={disabledDate}
              style={{ width: "100%" }}
              allowClear={false}
              format="DD/MM/YYYY"
              presets={[
                { label: "Últimos 7 dias", value: [moment().subtract(6, "days").startOf("day"), moment().endOf("day")] },
                { label: "Últimos 15 dias", value: [moment().subtract(14, "days").startOf("day"), moment().endOf("day")] },
                { label: "Últimos 31 dias", value: [moment().subtract(30, "days").startOf("day"), moment().endOf("day")] },
              ]}
              getPopupContainer={() => panelRef.current || document.body}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Typography.Text>Agrupar por departamento</Typography.Text>
              <Switch
                checked={group === "departamento"}
                onChange={(value) => {
                  setGroup(value ? "departamento" : "empresa");
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, width: "100%", marginTop: 4 }}>
              <button type="button" className="ant-btn" style={{ flex: 1 }} onClick={() => setModalFilter(false)}>
                <span>Cancelar</span>
              </button>
              <button type="button" className="ant-btn ant-btn-primary" style={{ flex: 1 }} onClick={handleApply}>
                <span>Filtrar</span>
              </button>
            </div>
          </div>
        )}
      </FilterPopover>

      {/* --- GRID UNIFICADO (dinâmico) --- */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: templateColumns,
          alignItems: "center",
          rowGap: ROW_GAP,
        }}
        className="hours-grid-unified"
      >
        {/* Cabeçalho das colunas de valores (começa na 3ª coluna) */}
        {headerLabels.map((label, idx) => (
          <div
            key={`hdr-${idx}`}
            style={{
              gridColumn: String(3 + idx),
              fontWeight: 700,
              fontSize: 14,
              color: "#101828"
            }}
          >
            {label}
          </div>
        ))}

        {/* Linhas */}
        {rows.map((row) => {
          const barVal = getBarValue(row);
          const barWidth = `${(barVal / maxAbsValue) * 100}%`;
          const barColor = hasDynamicCols
            ? "#0cc0fc"
            : (toNumber(row.saldo) >= 0 ? "#0cc0fc" : "#fac309");

          return (
            <React.Fragment key={row.ref_id}>
              {/* Coluna 1: Nome */}
              <div style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#101828",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}>
                {row.name}
              </div>

              {/* Coluna 2: Barra */}
              <div style={{
                height: BAR_SIZE,
                backgroundColor: "#ECEFF2",
                borderRadius: 10,
                overflow: "hidden",
                marginRight: 16
              }}>
                <div
                  style={{
                    width: barWidth,
                    height: "100%",
                    backgroundColor: barColor,
                    borderRadius: 10,
                    transition: "width 0.3s ease-out"
                  }}
                />
              </div>

              {/* Colunas de valores */}
              {valueCols.map((key) => (
                <div key={key} style={{ fontSize: 12, color: "#101828" }}>
                  {row[key]} h
                </div>
              ))}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
